<script setup lang="ts">
import { Commands, formatting, ViewContexts } from '@hantera/portal-app'
import { Spinner, TextInput, useClose } from '@hantera/design-system'
import { ref, watch } from 'vue'
import Decimal from 'decimal.js'
import useBackend from '@/composition/useBackend'


const props = defineProps<{
  orderViewContext: ViewContexts.OrderViewContext
}>()

interface PaymentHit {
  paymentId: string
  paymentNumber: string
  externalReference?: string
  providerKey: string
  authorizedAmount: Decimal
  chargedBalance: Decimal
  capturedBalance: Decimal
  creditBalance: Decimal
  currencyCode: string
}

const { graph } = useBackend()
const close = useClose()

const phrase = ref('')
const isLoading = ref(false)
const hits = ref<PaymentHit[]>([])
const linkingId = ref<string>()

let debouncer: ReturnType<typeof setTimeout> | undefined
let abortController: AbortController | undefined

watch(phrase, () => {
  if (debouncer) {
    clearTimeout(debouncer)
  }
  debouncer = setTimeout(search, 300)
})

async function search() {
  if (abortController) {
    abortController.abort()
  }

  if (phrase.value.trim().length === 0) {
    hits.value = []
    return
  }

  abortController = new AbortController()

  isLoading.value = true
  try {
    hits.value = await graph.queryGraph<PaymentHit>({
      edge: 'payments',
      phrase: phrase.value.trim() || undefined,
      orderBy: 'paymentNumber desc',
      limit: 15,
      node: {
        fields: [
          'paymentId',
          'paymentNumber',
          'externalReference',
          'providerKey',
          'authorizedAmount',
          'chargedBalance',
          'capturedBalance',
          'creditBalance',
          'currencyCode',
        ],
      },
    }, abortController.signal)
  } catch (e: any) {
    if (e?.name !== 'AbortError') {
      throw e
    }
  } finally {
    isLoading.value = false
  }
}

async function link(hit: PaymentHit) {
  linkingId.value = hit.paymentId

  const command: Commands.Order.LinkPaymentCommand = {
    type: 'linkPayment',
    paymentId: hit.paymentId,
  }

  const success = await props.orderViewContext.pushPendingChange(
    `linkPayment-${hit.paymentId}`,
    [command],
    'Linked payment {paymentNumber}',
    { paymentNumber: hit.paymentNumber }
  )

  linkingId.value = undefined

  if (success) {
    close()
  }
}

search()
</script>

<template>
  <div class="link-payment">
    <TextInput
      v-model="phrase"
      placeholder="Search by payment number or reference..."
      autofocus
    />

    <div class="link-payment__results option-list box-content neat-scroll">
      <div v-if="isLoading" class="link-payment__loading">
        <Spinner />
      </div>

      <div v-else-if="hits.length === 0" class="link-payment__empty">
        No payments found
      </div>

      <button
        v-for="hit in hits"
        v-else
        :key="hit.paymentId"
        class="option-list__item"
        :disabled="linkingId !== undefined"
        @click="link(hit)"
      >
        <div class="link-payment__hit-main">
          <div class="link-payment__hit-number">
            {{ hit.externalReference ?? hit.paymentNumber }}
          </div>
          <div v-if="hit.providerKey" class="link-payment__hit-provider">
            {{ hit.providerKey }}
          </div>
        </div>
        <div class="link-payment__details">
          <div class="link-payment__hit-amount">
            <div class="txt-s">Authorized</div>
            {{ formatting.currency(hit.authorizedAmount, hit.currencyCode) }}
          </div>
          <div class="link-payment__hit-amount">
            <div class="txt-s">Charged</div>
            {{ formatting.currency(hit.chargedBalance, hit.currencyCode) }}
          </div>
          <div class="link-payment__hit-amount">
            <div class="txt-s">Captured</div>
            {{ formatting.currency(hit.capturedBalance, hit.currencyCode) }}
          </div>
          <div class="link-payment__hit-amount">
            <div class="txt-s">Credit</div>
            {{ formatting.currency(hit.creditBalance, hit.currencyCode) }}
          </div>
        </div>
        
      </button>
    </div>
  </div>
</template>

<style scoped>
.link-payment {
  display: grid;
  grid-template-rows: min-content auto;
  gap: 10px;
  min-width: 400px;
  max-height: 60vh;
}

.link-payment__results {
  overflow-y: auto;
  height: 50vh
}

.link-payment__loading,
.link-payment__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.link-payment__hit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  cursor: pointer;
  width: 100%;
}

.link-payment__hit:hover:not(:disabled) {
  background-color: hsl(var(--color-80));
}

.link-payment__hit:disabled {
  opacity: 0.5;
  cursor: default;
}

button.option-list__item {
  flex-direction: column;
}

.link-payment__hit-main {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.link-payment__hit-number {
  font-weight: 600;
}

.link-payment__hit-provider {
  font-size: 12px;
}

.link-payment__details {
  width: 100%;
  display: flex;
  justify-content: space-between;
  text-align: right;
}
</style>
